import { Grid, makeStyles, Paper, Table, TableBody, TableCell, TableContainer, TableRow } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { findByKey } from '@thalesrc/js-utils';
import Reviewer from 'Accident/Accidents/Reviewer';
import Summary from 'Accident/Review/Summary';
import React, { useCallback, useContext, useState } from 'react';
import { useParams } from 'react-router';

import { getAccidentIdActions } from '../../../../../api/hs/accident/accident';
import Button from '../../../../../Common/Button';
import Dialog from '../../../../../Common/Dialog';
import { HSTableProps } from '../../../../../Common/Table/HSTable/hs-table.props';
import { ResourceContext } from '../../../../../context/resource.context';
import CustomAction from '../../../../../CustomAction';
import BaseSection from '../../../../../TransactionsAndLists/EmployeeList/Employee/BaseSection';
import { useDateFormat } from '../../../../../utils';
import AccidentEmployeesRecords from '../../../../AccidentEmployeesRecords';
import AccidentRecordsForm from '../../../../AccidentRecords/ListAdd/Form';
import { AccidentsDetailContext } from '../../accidents-detail.context';
import CommitteeMeetingFiles from '../CommitteeMeetingFiles';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    maxWidth: 752,
  },
  textColor: {
    color: '#737373',
    fontFamily: 'Segoe UI,Roboto,Oxygen,Ubuntu,Cantarell,Fira Sans,Droid Sans,Helvetica Neue,sans-serif;',
    fontSize: '15px',
  },
  textColor2: {
    fontFamily: 'Segoe UI,Roboto,Oxygen,Ubuntu,Cantarell,Fira Sans,Droid Sans,Helvetica Neue,sans-serif;',
    fontSize: '16px',
  },
  demo: {
    backgroundColor: theme.palette.background.paper,
  },
  avatar: {
    marginLeft: '100px',
    width: theme.spacing(15),
    height: theme.spacing(15),
  },
  aside: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
}));

function DateField({ date }: { date: Date }) {
  const format = useDateFormat();

  return <>{format(date, 'dd MMMM yyyy HH:mm')}</>;
}

export default function Index() {
  const { accidentDetail, reload, accidentReview } = useContext(AccidentsDetailContext);
  const { companies } = useContext(ResourceContext);

  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const { accidentId } = useParams<{ accidentId: string }>();
  const fetchActionList = useCallback<HSTableProps<any>['fetcher']>(
    (page, size, sorting, extra) => {
      if (!accidentId) return;
      return getAccidentIdActions(
        accidentId,
        page,
        size,
        sorting === false ? null : `${sorting.id as string}-${sorting.direction.toUpperCase()}`
      );
    },
    [accidentId]
  );
  const onCloseDialog = useCallback(
    async (success: boolean) => {
      if (success) {
        await reload();
        setOpen(false);
      }
      setOpen(false);
    },
    [setOpen, reload]
  );

  const showAccidentRecordForm = useCallback(() => {
    setOpen(true);
  }, []);

  return (
    <Paper className="m-1" elevation={2}>
      <Grid container spacing={1}>
        <Grid item sm={12} md={6} lg={6} xs={12}>
          <TableContainer>
            <Table size="small" aria-label="custom pagination table">
              <TableBody>
                <TableRow>
                  <TableCell width="50%" className={classes.textColor} align="left">
                    Şirket
                  </TableCell>
                  <TableCell width="50%" className={classes.textColor2} align="left">
                    {findByKey(companies, 'id', accidentDetail?.companyId)?.companyName}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width="50%" component="th" scope="row" className={classes.textColor}>
                    İstasyon
                  </TableCell>
                  <TableCell width="50%" className={classes.textColor2} align="left">
                    {accidentDetail?.accidentStationViews?.map(station => station.stationName)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width="50%" component="th" scope="row" className={classes.textColor}>
                    Vardiya
                  </TableCell>
                  <TableCell width="50%" className={classes.textColor2} align="left">
                    {accidentDetail?.shiftDescription}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item sm={12} md={6} lg={6} xs={12}>
          <TableContainer>
            <Table size="small" aria-label="custom pagination table">
              <TableBody>
                <TableRow>
                  <TableCell width="50%" component="th" scope="row" className={classes.textColor}>
                    Departman{' '}
                  </TableCell>
                  <TableCell width="50%" className={classes.textColor2} align="left">
                    {accidentDetail?.accidentDepartmentViews?.map(department => department.departmentName)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width="50%" component="th" scope="row" className={classes.textColor}>
                    Kaza Türü
                  </TableCell>
                  <TableCell width="50%" component="th" scope="row" className={classes.textColor2}>
                    {accidentDetail?.accidentTypeViews?.map(accidentType => accidentType.typeDefinitionLabel)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width="50%" className={classes.textColor} component="th" scope="row">
                    Tarih
                  </TableCell>
                  <TableCell width="50%" className={classes.textColor2} component="th" scope="row">
                    {accidentDetail?.date ? <DateField date={new Date(accidentDetail?.date)} /> : null}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <div className="text-right mt-1 mr-2 mb-1">
        <Button onClick={showAccidentRecordForm} variant="contained" startIcon={<EditIcon />} color="primary" className="ml-1">
          Kaza Bilgilerini Düzenle
        </Button>
      </div>
      <BaseSection icon="icon-flash" title="Kaza Geçiren Personel Listesi">
        {accidentDetail && <AccidentEmployeesRecords accidentId={accidentDetail.accidentId} />}
      </BaseSection>
      <Dialog color="secondary" title={'Kaza Düzenle'} opened={open} onClose={onCloseDialog}>
        <AccidentRecordsForm data={accidentDetail} onClose={onCloseDialog} />
      </Dialog>
      <BaseSection icon="icon-flash" title="Kaza Aksiyonları">
        <CustomAction fetcher={fetchActionList} referenceId={accidentDetail.accidentId} actionReferenceValue={'ACCIDENT'} />
      </BaseSection>
      <BaseSection icon="icon-attachment" title={`Dosya`}>
        <CommitteeMeetingFiles />
      </BaseSection>
      <BaseSection icon="icon-people" title="Kaza İnceleme Ekibi">
        <Reviewer accidentId={accidentDetail?.accidentId} />
      </BaseSection>
      {accidentReview && (
        <BaseSection icon="icon-flash" title="Kaza İnceleme">
          <Summary accidentId={accidentDetail?.accidentId} />
        </BaseSection>
      )}
    </Paper>
  );
}
